import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import {
  ColDef,
  GetContextMenuItems,
  GridApi,
  IServerSideDatasource,
  MenuItemDef,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GridStatusBar, WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PrimaryContext } from '@/services';
import { hasPermission, MODULES, useCurrentUser } from '@/services/auth/util';
import { EmbryoDto } from '@/services/embryo-service';
import {
  auditHistoryMenuItem,
  DEFAULT_CONTEXT_MENU,
} from '@/utils/context-menu';

import { useEmbryoAppContext } from '../../context/EmbryoAppContext';
import { EmbryoFilters } from '../../util/types';

interface EmbryoGridProps {
  columnDefs: ColDef[];
  storageKey: string;
  gridClassName?: string;
  dataSource: IServerSideDatasource;
  filters: EmbryoFilters;
  noRowsMessage?: string;
  onGridReady?: (api: { refresh: () => void; deselectAll: () => void }) => void;
  onDeactivateSelected?: () => void;
}

export const EmbryoGrid = ({
  columnDefs,
  storageKey,
  gridClassName = 'embryo-grid',
  dataSource,
  filters,
  noRowsMessage,
  onGridReady,
  onDeactivateSelected,
}: EmbryoGridProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const isDefaultFiltersEnabled = useFeatureFlag('Embryo_Default_Filters');
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const navigate = useNavigate();
  const { setSelectedEmbryos } = useEmbryoAppContext();
  const gridRef = useRef<AgGridReact>(null);
  const { data: currentUser } = useCurrentUser();
  const canManageLifecycle = hasPermission(
    currentUser,
    MODULES.EMBRYO_MANAGER_WRITE,
  );
  const handleStorageLoad = useCallback(
    (api: GridApi) => {
      if (!isDefaultFiltersEnabled) return;
      const current = api.getFilterModel() ?? {};
      const defaults: Record<string, unknown> = {};
      if (!current.status) {
        defaults.status = { filterType: 'set', values: ['1'] };
      }
      if (!current.stateCode) {
        defaults.stateCode = { filterType: 'set', values: ['0'] };
      }
      if (Object.keys(defaults).length > 0) {
        api.setFilterModel({ ...current, ...defaults });
      }
    },
    [isDefaultFiltersEnabled],
  );

  const resolvedColumnDefs = useMemo(() => {
    if (!isDefaultFiltersEnabled) return columnDefs;

    return columnDefs.map((colDef) => {
      if (colDef.field !== 'status' && colDef.field !== 'stateCode') {
        return colDef;
      }

      return {
        ...colDef,
        filterParams: {
          ...colDef.filterParams,
          defaultToNothingSelected: true,
          keyCreator: (params: { value: unknown }) => String(params.value),
        },
      };
    });
  }, [columnDefs, isDefaultFiltersEnabled]);

  const getContextMenuItems = useCallback<GetContextMenuItems>(
    (params) => {
      const rowId: string | undefined = params.node?.data?.id;
      if (!rowId) return [];

      const items: (MenuItemDef | string)[] = [
        auditHistoryMenuItem({ rowId }),
        'separator',
      ];

      if (canManageLifecycle) {
        const selectedRows = params.api.getSelectedRows() as EmbryoDto[];
        const rows =
          selectedRows.length > 1 ? selectedRows : [params.node!.data];
        const stateCodes = new Set(rows.map((r) => r.stateCode));

        if (stateCodes.size === 1) {
          const isDeactivated = [...stateCodes][0] === 1;
          items.push({
            name: isDeactivated ? 'Reactivate' : 'Deactivate',
            icon: '<i class="pi pi-power-off" />',
            action() {
              setSelectedEmbryos(rows);
              onDeactivateSelected?.();
            },
          });
          items.push('separator');
        }
      }

      items.push(...DEFAULT_CONTEXT_MENU);
      return items;
    },
    [canManageLifecycle, onDeactivateSelected, setSelectedEmbryos],
  );

  const refreshGridWithFilters = useCallback(() => {
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  useEffect(() => {
    refreshGridWithFilters();
  }, [filters, refreshGridWithFilters]);

  const handleGridReady = () => {
    onGridReady?.({
      refresh: refreshGridWithFilters,
      deselectAll: () => gridRef.current?.api?.deselectAll(),
    });
  };

  const handleRowDoubleClicked = useCallback(
    (e: RowDoubleClickedEvent) => {
      const embryoId = e.data?.id ?? e.data?.embryoId;
      if (embryoId) {
        const isOutsideEmb = gridClassName === 'outside-embryo-app';
        const typeParam = isOutsideEmb ? '&type=outside' : '';
        navigate(`/embryo/embryo?id=${embryoId}${typeParam}`);
      }
    },
    [navigate, gridClassName],
  );

  const handleSelectionChanged = useCallback(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows() as
      | EmbryoDto[]
      | undefined;
    if (selectedRows && selectedRows.length > 0) {
      setSelectedEmbryos(selectedRows);
    } else {
      setSelectedEmbryos([]);
    }
  }, [setSelectedEmbryos]);

  return (
    <div className="h-[calc(100vh-350px)] w-full">
      <WithLocalStorage storageKey={storageKey} onLoad={handleStorageLoad}>
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={gridRef}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} ${gridClassName}`.trim()}
          columnDefs={resolvedColumnDefs}
          getRowId={(params) => String(params.data.id)}
          onGridReady={handleGridReady}
          defaultColDef={defaultColDef}
          onRowDoubleClicked={handleRowDoubleClicked}
          onSelectionChanged={handleSelectionChanged}
          rowSelection="multiple"
          suppressRowClickSelection={false}
          rowModelType="serverSide"
          cacheBlockSize={20}
          blockLoadDebounceMillis={500}
          serverSideDatasource={dataSource}
          sideBar={sideBar}
          getContextMenuItems={getContextMenuItems}
          overlayNoRowsTemplate={noRowsMessage || t('embryo.noEmbryosFound')}
          overlayLoadingTemplate={`<div class="flex flex-col items-center gap-2"><span class="inline-block h-8 w-8 animate-spin rounded-full border-3 border-gray-400 border-t-transparent"></span><span class="leading-none text-gray-500">${t('common.loading')}</span></div>`}
          statusBar={{
            statusPanels: [
              {
                statusPanel: GridStatusBar,
                statusPanelParams: { entityLabel: 'embryos' },
                align: 'left' as const,
              },
            ],
          }}
        />
      </WithLocalStorage>
    </div>
  );
};
